/**
 * @fileoverview Shared auth failure classification, telemetry, Sentry, toasts, and retry for onboarding sign-up / sign-in.
 */

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { toast } from "sonner";
import { trackOnboarding } from "@/lib/telemetry";

const MAX_AUTH_RETRIES = 3;

export type AuthFlowMethod = "google" | "email" | "magic-link";

export type AuthFlowErrorType =
  | "USER_ALREADY_EXISTS"
  | "SIGNUP_FAILED"
  | "TURNSTILE_FAILED"
  | "TURNSTILE_MISSING"
  | "TURNSTILE"
  | "credential_mismatch"
  | "email_not_verified"
  | "network"
  | "server_5xx"
  | "rate_limit"
  | "validation"
  | "unknown";

type AuthFlowKind = "signup" | "signin";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function truncateMessage(message: string, max: number): string {
  if (message.length <= max) {
    return message;
  }
  return `${message.slice(0, max)}…`;
}

function readStringRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

function networkHintFromMessage(lower: string): boolean {
  return (
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("load failed") ||
    lower.includes("network request failed")
  );
}

function normalizeFromErrorInstance(err: Error): {
  message: string;
  code?: string;
  status?: number;
} {
  const msg = err.message;
  const lower = msg.toLowerCase();
  if (networkHintFromMessage(lower)) {
    return { message: msg, status: 0 };
  }
  return { message: msg };
}

function normalizeFromRecord(o: Record<string, unknown>): {
  message: string;
  code?: string;
  status?: number;
} {
  const inner = readStringRecord(o.error);
  if (inner) {
    const status = inner.status;
    const code = inner.code;
    return {
      message: String(inner.message ?? inner.statusText ?? ""),
      code: typeof code === "string" ? code : undefined,
      status: typeof status === "number" ? status : undefined,
    };
  }
  const status = o.status;
  const code = o.code;
  return {
    message: String(o.message ?? o.statusText ?? ""),
    code: typeof code === "string" ? code : undefined,
    status: typeof status === "number" ? status : undefined,
  };
}

/**
 * Normalizes Better Auth / better-fetch errors and thrown values into a common shape.
 */
export function normalizeAuthError(err: unknown): {
  message: string;
  code?: string;
  status?: number;
} {
  if (err === null || err === undefined) {
    return { message: "unknown_error" };
  }
  if (typeof err === "string") {
    return { message: err };
  }
  if (err instanceof Error) {
    return normalizeFromErrorInstance(err);
  }
  const o = readStringRecord(err);
  if (!o) {
    return { message: String(err) };
  }
  return normalizeFromRecord(o);
}

function classifyTurnstileBranch(c: string, m: string): AuthFlowErrorType | null {
  if (c.startsWith("TURNSTILE")) {
    if (c === "TURNSTILE_FAILED") {
      return "TURNSTILE_FAILED";
    }
    if (c === "TURNSTILE_MISSING") {
      return "TURNSTILE_MISSING";
    }
    return "TURNSTILE";
  }
  if (m.includes("turnstile") || m.includes("captcha")) {
    return "TURNSTILE";
  }
  return null;
}

function classifyUserConflictBranch(c: string, m: string): AuthFlowErrorType | null {
  if (
    c.includes("USER_ALREADY") ||
    c === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL" ||
    c === "FAILED_TO_CREATE_USER" ||
    m.includes("already exists") ||
    m.includes("use another email")
  ) {
    return "USER_ALREADY_EXISTS";
  }
  if (c === "SIGNUP_FAILED") {
    return "SIGNUP_FAILED";
  }
  return null;
}

function isCredentialMismatch(c: string, m: string, status: number | undefined): boolean {
  if (c === "INVALID_EMAIL_OR_PASSWORD") {
    return true;
  }
  if (status === 401 && (m.includes("invalid email or password") || m.includes("invalid_email_or_password"))) {
    return true;
  }
  return false;
}

function isEmailNotVerified(c: string, m: string, status: number | undefined): boolean {
  if (c === "EMAIL_NOT_VERIFIED") {
    return true;
  }
  if (status === 403 && (m.includes("email not verified") || m.includes("not verified"))) {
    return true;
  }
  return false;
}

function classifyByStatusAndMessage(
  status: number | undefined,
  m: string,
  c: string,
): AuthFlowErrorType {
  if (isCredentialMismatch(c, m, status)) {
    return "credential_mismatch";
  }
  if (status !== undefined && status >= 500) {
    return "server_5xx";
  }
  if (status === 429 || m.includes("too many") || m.includes("rate limit")) {
    return "rate_limit";
  }
  if (status === 400 || status === 422 || c.includes("INVALID") || m.includes("invalid email")) {
    return "validation";
  }
  if (
    status === 0 ||
    networkHintFromMessage(m) ||
    m.includes("network") ||
    m.includes("load failed") ||
    m.includes("network request failed")
  ) {
    return "network";
  }
  return "unknown";
}

export function classifyAuthFlowError(payload: {
  message: string;
  code?: string;
  status?: number;
}): AuthFlowErrorType {
  const { message, code, status } = payload;
  const m = message.toLowerCase();
  const c = (code ?? "").toUpperCase();

  const turnstile = classifyTurnstileBranch(c, m);
  if (turnstile !== null) {
    return turnstile;
  }

  const conflict = classifyUserConflictBranch(c, m);
  if (conflict !== null) {
    return conflict;
  }

  if (isEmailNotVerified(c, m, status)) {
    return "email_not_verified";
  }

  return classifyByStatusAndMessage(status, m, c);
}

export function isAuthFlowErrorRetryable(type: AuthFlowErrorType, status?: number): boolean {
  if (type === "network") {
    return true;
  }
  if (type === "server_5xx") {
    return true;
  }
  if (status !== undefined && status >= 500) {
    return true;
  }
  return false;
}

function getAuthFailureToast(
  errorType: AuthFlowErrorType,
  flow: AuthFlowKind,
): { title: string; description?: string } {
  switch (errorType) {
    case "credential_mismatch":
      return {
        title: i18n._(msg`Unable to sign in`),
        description: i18n._(
          msg`If you signed up with Google, use Continue with Google. Otherwise, try resetting your password or check your email for a verification link.`,
        ),
      };
    case "email_not_verified":
      return {
        title: i18n._(msg`Verify your email to sign in`),
        description: i18n._(
          msg`Your password is correct, but this email isn't verified yet. Check your inbox for the verification link, or request a new one.`,
        ),
      };
    case "USER_ALREADY_EXISTS":
    case "SIGNUP_FAILED":
      return {
        title: i18n._(msg`This email may already be in use`),
        description:
          flow === "signup"
            ? i18n._(msg`Sign in instead, or try a different email address.`)
            : i18n._(msg`Use Google, password, or a magic link if you already have an account.`),
      };
    case "TURNSTILE_FAILED":
    case "TURNSTILE_MISSING":
    case "TURNSTILE":
      return {
        title: i18n._(msg`Security check failed`),
        description: i18n._(msg`Refresh the page and complete the verification again.`),
      };
    case "network":
      return {
        title: i18n._(msg`Connection problem`),
        description: i18n._(msg`Check your network. We retried a few times automatically.`),
      };
    case "server_5xx":
      return {
        title: i18n._(msg`Our servers had a problem`),
        description: i18n._(msg`Please try again shortly. We retried automatically.`),
      };
    case "rate_limit":
      return {
        title: i18n._(msg`Too many attempts`),
        description: i18n._(msg`Wait a minute, then try again.`),
      };
    case "validation":
      return {
        title: i18n._(msg`Check your details`),
        description: i18n._(msg`Fix the highlighted fields and try again.`),
      };
    default:
      return {
        title: i18n._(msg`Something went wrong`),
        description: i18n._(msg`Please try again. If it keeps happening, contact support.`),
      };
  }
}

async function captureAuthExceptionSentry(
  error: unknown,
  tags: { flow: AuthFlowKind; method: AuthFlowMethod; errorType: string },
): Promise<void> {
  try {
    const Sentry = await import("@sentry/browser");
    const err = error instanceof Error ? error : new Error(safeSerializeUnknown(error));
    Sentry.captureException(err, {
      tags: {
        auth_flow: tags.flow,
        auth_method: tags.method,
        auth_error_type: tags.errorType,
      },
    });
  } catch {
    // Sentry not available in this environment
  }
}

function safeSerializeUnknown(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error);
}

async function reportAuthFailure(
  flow: AuthFlowKind,
  method: AuthFlowMethod,
  error: unknown,
): Promise<{ errorType: AuthFlowErrorType; toast: { title: string; description?: string } }> {
  const normalized = normalizeAuthError(error);
  const errorType = classifyAuthFlowError(normalized);
  const messageForTelemetry = truncateMessage(normalized.message, 500);

  if (flow === "signup") {
    trackOnboarding.signupFailed(method, errorType, messageForTelemetry);
  } else {
    trackOnboarding.signinFailed(method, errorType, messageForTelemetry);
  }

  await captureAuthExceptionSentry(error, { flow, method, errorType });

  return {
    errorType,
    toast: getAuthFailureToast(errorType, flow),
  };
}

function showAuthFailureToast(t: { title: string; description?: string }): void {
  if (t.description) {
    toast.error(t.title, { description: t.description });
    return;
  }
  toast.error(t.title);
}

export async function handleSignupAuthFailure(
  method: AuthFlowMethod,
  error: unknown,
): Promise<void> {
  const { toast: t } = await reportAuthFailure("signup", method, error);
  showAuthFailureToast(t);
}

export async function handleSigninAuthFailure(
  method: AuthFlowMethod,
  error: unknown,
): Promise<void> {
  const { toast: t } = await reportAuthFailure("signin", method, error);
  showAuthFailureToast(t);
}

export type AuthCallbackResult =
  | { outcome: "success" }
  | { outcome: "error"; error: unknown; retryable: boolean };

/**
 * Runs a Better Auth callback-style call with up to 3 attempts on network / 5xx.
 */
export async function runAuthCallbackWithRetries(
  execute: () => Promise<AuthCallbackResult>,
): Promise<{ success: boolean; lastError?: unknown }> {
  const attempt = async (n: number): Promise<{ success: boolean; lastError?: unknown }> => {
    const r = await execute();
    if (r.outcome === "success") {
      return { success: true };
    }
    if (r.retryable && n < MAX_AUTH_RETRIES) {
      await sleep(250 * n);
      return attempt(n + 1);
    }
    return { success: false, lastError: r.error };
  };
  return attempt(1);
}

export async function runGoogleAuthWithRetries(
  flow: AuthFlowKind,
  method: AuthFlowMethod,
  run: () => Promise<void>,
): Promise<boolean> {
  const runAttempt = async (n: number): Promise<boolean> => {
    try {
      await run();
      return true;
    } catch (error) {
      const normalized = normalizeAuthError(error);
      const type = classifyAuthFlowError(normalized);
      if (isAuthFlowErrorRetryable(type, normalized.status) && n < MAX_AUTH_RETRIES) {
        await sleep(250 * n);
        return runAttempt(n + 1);
      }
      if (flow === "signup") {
        await handleSignupAuthFailure(method, error);
      } else {
        await handleSigninAuthFailure(method, error);
      }
      return false;
    }
  };
  return runAttempt(1);
}
